import React, { ReactNode, useMemo } from 'react';
import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { LenderProfiles } from '@trnsact/trnsact-shared-types/src/generated';
import { FinanceProgramForm } from '../../schema';
import { Confirm } from './formSteps/confirm/Confirm';
import { ProgramDetails } from './formSteps/details/ProgramDetails';
import { ChooseCriteria } from './formSteps/criteria/ChooseCriteria';
import { FinanceCriteriaOptions, FinanceProgramFormSteps } from '../../types';
import { ConfigureStructure } from './formSteps/structure';
import { InsuranceCoverageRequirements } from './formSteps/insurance/InsuranceCoverageRequirements';
import { SubmissionSettings } from './formSteps/submission/SubmissionSettings';
import { ScoreTierEntity } from 'pages/LenderWaterfall/ScorecardBuilder/types';

interface Props {
  isReadOnly: boolean;
  isMultipleLenders?: boolean;
  userType?: 'lender' | 'dealer';
  scoreTiers?: ScoreTierEntity[];
  lendersOptions: LenderProfiles[];
  isLendersSelectDisabled?: boolean;
  dealerVendorProfileOptions?: any[];
  criteriaOptions: FinanceCriteriaOptions[];
  lpsWithOcaAccessByLenderProfileId?: Record<string, LenderProfiles>;
}

export const Form = ({
  userType,
  isReadOnly,
  scoreTiers,
  lendersOptions,
  criteriaOptions,
  isMultipleLenders,
  isLendersSelectDisabled,
  dealerVendorProfileOptions,
  lpsWithOcaAccessByLenderProfileId = {},
}: Props) => {
  const { control, getValues } = useFormContext<FinanceProgramForm>();
  const currentStepWatchValue = useWatch({ control, name: 'step' });

  const selectedLenders = useWatch({ control, name: 'program.first.lenders' }) as LenderProfiles[];
  const isLenderCreditAppSettingVisible = useMemo(
    () => selectedLenders?.some(l => !!lpsWithOcaAccessByLenderProfileId[l.id!]),
    [selectedLenders]
  );

  const renderFormStep: Record<FinanceProgramFormSteps, ReactNode> = {
    [FinanceProgramFormSteps.First]: (
      <ProgramDetails
        userType={userType}
        isReadOnly={isReadOnly}
        lendersOptions={lendersOptions}
        isMultipleLenders={isMultipleLenders}
        isLendersSelectDisabled={isLendersSelectDisabled}
        dealerVendorProfileOptions={dealerVendorProfileOptions}
        isLenderCreditAppSettingVisible={isLenderCreditAppSettingVisible}
      />
    ),
    [FinanceProgramFormSteps.Second]: <ConfigureStructure isReadOnly={isReadOnly} scoreTiers={scoreTiers} />,
    [FinanceProgramFormSteps.Third]: (
      <ChooseCriteria
        isReadOnly={isReadOnly}
        userType={userType}
        criteriaOptions={criteriaOptions}
        dealerVendorProfileOptions={dealerVendorProfileOptions}
      />
    ),
    [FinanceProgramFormSteps.Fourth]: <SubmissionSettings isReadOnly={isReadOnly} />,
    [FinanceProgramFormSteps.Fifth]: <InsuranceCoverageRequirements isReadOnly={isReadOnly} />,
    [FinanceProgramFormSteps.Sixth]: (
      <Confirm
        financeProgramFormValues={getValues('program')}
        isLenderCreditAppSettingVisible={isLenderCreditAppSettingVisible}
      />
    ),
  };

  return <Box className="formSection">{renderFormStep[currentStepWatchValue]}</Box>;
};
